import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { countries } from '../utils/countries';
import { openReceiptWindow } from './CreateReceipt';
import './FCLabel.css';

/* ──────────────────────────────────────────────
   Constants
────────────────────────────────────────────── */
const API = process.env.NODE_ENV === 'development'
    ? 'http://localhost:5001/api'
    : '/api';

const PRODUCT_TYPES = [
    { code: 'NON-DOCUMENTS', label: 'Non-Document', icon: '📦' },
    { code: 'DOCUMENTS', label: 'Document', icon: '📄' },
    { code: 'LETTERS', label: 'Letter', icon: '✉️' }
];

const GST_OPTIONS = [
    { label: '15%', value: '15pct' },
    { label: '16%', value: '16pct' },
    { label: '0%', value: '0pct' },
    { label: 'Fixed 1000 PKR', value: 'fixed1000' }
];

const EMPTY_ADDR = {
    name: '', attention: '', phone: '', email: '',
    address1: '', address2: '', city: '', state: '', postalCode: '', country: 'US'
};

const EMPTY_DEST = {
    name: '', attention: '', phone: '', email: '',
    address1: '', address2: '', city: '', state: '', postalCode: '', country: 'PK'
};

const EMPTY_PKG = { weight: '', length: '', width: '', height: '', description: '', additionalHandling: false };

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
const getCountryName = (code) => {
    if (!code) return '';
    const c = countries.find(x => x.code === code);
    return c ? c.name : code;
};

const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
};

const fmt2 = (n) => (parseFloat(n) || 0).toFixed(2);

const calcGst = (subtotalUSD, gstOption, exchangeRate) => {
    const ex = parseFloat(exchangeRate) || 280.50;
    if (gstOption === '15pct') return { usd: subtotalUSD * 0.15, pkr: null };
    if (gstOption === '16pct') return { usd: subtotalUSD * 0.16, pkr: null };
    if (gstOption === '0pct') return { usd: 0, pkr: 0 };
    if (gstOption === 'fixed1000') return { usd: 1000 / ex, pkr: 1000 };
    return { usd: 0, pkr: 0 };
};

/* ──────────────────────────────────────────────
   ADDRESS BLOCK — outside component
────────────────────────────────────────────── */
const AddrBlock = ({ label, icon, data, onChange, colorBorder, colorBg, colorText }) => (
    <div style={{
        border: `2px solid ${colorBorder}`, borderRadius: 14, overflow: 'hidden', marginBottom: 16
    }}>
        <div style={{
            background: colorBg, padding: '8px 18px',
            fontSize: 11, fontWeight: 700, color: colorText, letterSpacing: '0.5px'
        }}>
            {icon} {label}
        </div>
        <div style={{ padding: '14px 18px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div className="fc-field">
                    <label>Full Name / Company *</label>
                    <input value={data.name} onChange={e => onChange('name', e.target.value)} placeholder="John Smith or ABC Corp" />
                </div>
                <div className="fc-field">
                    <label>Attention / Contact</label>
                    <input value={data.attention} onChange={e => onChange('attention', e.target.value)} placeholder="Contact person" />
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div className="fc-field">
                    <label>Phone</label>
                    <input value={data.phone} onChange={e => onChange('phone', e.target.value)} placeholder="+1 555 000 0000" />
                </div>
                <div className="fc-field">
                    <label>Email</label>
                    <input type="email" value={data.email} onChange={e => onChange('email', e.target.value)} placeholder="email@example.com" />
                </div>
                <div className="fc-field">
                    <label>Country *</label>
                    <select value={data.country} onChange={e => onChange('country', e.target.value)}>
                        {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div className="fc-field">
                    <label>Address Line 1 *</label>
                    <input value={data.address1} onChange={e => onChange('address1', e.target.value)} placeholder="Street address" />
                </div>
                <div className="fc-field">
                    <label>Address Line 2</label>
                    <input value={data.address2} onChange={e => onChange('address2', e.target.value)} placeholder="Apt, Suite, etc." />
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div className="fc-field">
                    <label>City *</label>
                    <input value={data.city} onChange={e => onChange('city', e.target.value)} placeholder="City" />
                </div>
                <div className="fc-field">
                    <label>State / Province</label>
                    <input value={data.state} onChange={e => onChange('state', e.target.value)} placeholder="CA, NY…" />
                </div>
                <div className="fc-field">
                    <label>Postal Code</label>
                    <input value={data.postalCode} onChange={e => onChange('postalCode', e.target.value)} placeholder="Zip / Postal" />
                </div>
            </div>
        </div>
    </div>
);

/* ──────────────────────────────────────────────
   Label Extractor Helper
────────────────────────────────────────────── */
const extractLabels = (item) => {
    if (!item) return [];
    let list = [];
    if (Array.isArray(item.labels) && item.labels.length > 0) {
        list = item.labels;
    } else if (item.labelImage) {
        list = [item.labelImage];
    }

    const result = [];
    for (const entry of list) {
        if (!entry) continue;
        if (typeof entry === 'object' && entry.labelImage) {
            const img = String(entry.labelImage).trim();
            if (img && img !== '(available)' && img !== 'placeholder' && img.length > 50) {
                result.push(img);
            }
        } else if (typeof entry === 'string') {
            const img = entry.trim();
            if (img && img !== '(available)' && img !== 'placeholder' && img.length > 50) {
                result.push(img);
            }
        }
    }

    if (result.length === 0 && item.labelImage && typeof item.labelImage === 'string') {
        const img = item.labelImage.trim();
        if (img && img !== '(available)' && img !== 'placeholder' && img.length > 50) {
            result.push(img);
        }
    }

    return result;
};

/* ──────────────────────────────────────────────
   Label Result Modal
────────────────────────────────────────────── */
const LabelResultModal = ({ labelResult, onClose, onPrint, onDownload, onDownloadReceipt, onDownloadCombined }) => {
    if (!labelResult) return null;

    if (labelResult.loading) {
        return (
            <div className="fc-form-overlay" onClick={onClose}>
                <div className="fc-result-modal" onClick={e => e.stopPropagation()}>
                    <div className="fc-result-head">
                        <h3>⏳ Fetching FC Label...</h3>
                        <button className="fc-close-btn" onClick={onClose}>✕</button>
                    </div>
                    <div className="fc-tracking-number">📦 {labelResult.trackingNumber || 'Loading...'}</div>
                    <div style={{ textAlign: 'center', padding: '36px 20px' }}>
                        <div className="fc-spinner" style={{ width: 36, height: 36, margin: '0 auto 16px', borderWidth: 4 }} />
                        <p style={{ fontSize: 13, color: '#475569', fontWeight: 600, margin: 0 }}>
                            Loading label image from UPS records...
                        </p>
                    </div>
                    <div className="fc-result-actions">
                        <button className="fc-result-btn close" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    const labels = extractLabels(labelResult);
    const hasLabels = labels.length > 0;

    return (
        <div className="fc-form-overlay" onClick={onClose}>
            <div className="fc-result-modal" onClick={e => e.stopPropagation()}>
                <div className="fc-result-head">
                    <h3>{hasLabels ? '✅ FC Label Ready' : '⚠️ Label Not Available'}</h3>
                    <button className="fc-close-btn" onClick={onClose}>✕</button>
                </div>
                <div className="fc-tracking-number">📦 {labelResult.trackingNumber || 'Tracking N/A'}</div>
                <p style={{ fontSize: 12, color: '#64748b', textAlign: 'center', margin: '0 22px 8px' }}>
                    {hasLabels
                        ? 'Provide this label to your customer so they can ship the parcel to Pakistan.'
                        : (labelResult.error || 'No label image returned. Please check UPS configuration.')}
                </p>
                {hasLabels && (
                    <div className="fc-label-preview" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {labels.map((l, idx) => {
                            const src = l.startsWith('data:') ? l : `data:image/gif;base64,${l}`;
                            return (
                                <div key={idx} style={{ marginBottom: labels.length > 1 ? 16 : 0, textAlign: 'center' }}>
                                    {labels.length > 1 && (
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', marginBottom: 4 }}>
                                            Package {idx + 1} of {labels.length}
                                        </div>
                                    )}
                                    <img src={src} alt={`FC Label ${idx + 1}`} style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                                </div>
                            );
                        })}
                    </div>
                )}
                {!hasLabels && (
                    <div className="fc-alert error" style={{ margin: '12px 22px' }}>
                        ⚠️ {labelResult.error || 'No label image returned. Please check UPS configuration.'}
                    </div>
                )}
                <div className="fc-result-actions">
                    {hasLabels && (
                        <>
                            <button className="fc-result-btn print" onClick={() => onPrint(labels)}>🖨️ Print Label</button>
                            <button className="fc-result-btn download" onClick={() => onDownload(labels, labelResult.trackingNumber)}>⬇️ Download Label</button>
                            <button className="fc-result-btn download" onClick={() => onDownloadReceipt(labelResult)}>📄 Download Receipt</button>
                            <button className="fc-result-btn download" onClick={() => onDownloadCombined(labels, labelResult)}>📦 Download Combined</button>
                        </>
                    )}
                    <button className="fc-result-btn close" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

/* ──────────────────────────────────────────────
   IMPORT ACCOUNT CARD
────────────────────────────────────────────── */
const ImportAccountCard = ({
    account, isSelected, onSelect,
    formReady, shipFrom, shipTo, packages, productType, generalDescription, reference1, reference2,
    declaredValue, originNotify, destinationNotify,
    currentUser, companySlug,
    onLabelCreated
}) => {
    const [cardState, setCardState] = useState('idle'); // idle | rating | rated | paying | paid | creating | done
    const [quote, setQuote] = useState(null);
    const [gstOption, setGstOption] = useState('15pct');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [transactionIdState, setTransactionIdState] = useState('');
    const [error, setError] = useState('');
    const [labelResult, setLabelResult] = useState(null);

    // Derived: total weight
    const totalWeight = packages.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
    const returnShipmentCharge = 0;
    const exc = quote?.exchangeRate || 280.50;

    // Compute GST on current subtotal
    const gstCalc = quote
        ? calcGst(quote.charges.subtotalUSD, gstOption, exc)
        : { usd: 0, pkr: 0 };

    const grandTotalUSD = quote
        ? quote.charges.subtotalUSD + gstCalc.usd + returnShipmentCharge
        : 0;

    const toRow = (label, usd, pkr) => (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '5px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12
        }}>
            <span style={{ color: '#64748b' }}>{label}</span>
            <span>
                <strong style={{ color: '#1e293b' }}>${fmt2(usd)}</strong>
                <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>
                    (PKR {fmt2(pkr != null ? pkr : usd * exc)})
                </span>
            </span>
        </div>
    );

    const handleGetRate = async () => {
        if (!formReady) {
            setError('Please fill Ship From country and package weight first.');
            return;
        }
        setError('');
        setCardState('rating');
        try {
            // 1) SearchZone call for Origin (Ship From)
            let originRemote = 0;
            try {
                const originRes = await axios.post(`${API}/check-remote-area`, {
                    city: shipFrom.city || '',
                    state: shipFrom.state || '',
                    country: shipFrom.country || 'US',
                    postalCode: shipFrom.postalCode || ''
                });
                if (originRes.data.success && originRes.data.isRemote) {
                    originRemote = parseFloat(originRes.data.totalSurcharge || 0);
                }
            } catch (e) { console.warn('Origin remote check failed', e); }

            // 2) SearchZone call for Destination (Ship To)
            let destRemote = 0;
            try {
                const destRes = await axios.post(`${API}/check-remote-area`, {
                    city: shipTo.city || '',
                    state: shipTo.state || '',
                    country: shipTo.country || 'PK',
                    postalCode: shipTo.postalCode || ''
                });
                if (destRes.data.success && destRes.data.isRemote) {
                    destRemote = parseFloat(destRes.data.totalSurcharge || 0);
                }
            } catch (e) { console.warn('Dest remote check failed', e); }

            const res = await axios.post(`${API}/fc-label/get-rate`, {
                accountNumber: account,
                product: productType,
                originCountry: shipFrom.country,
                originPostalCode: shipFrom.postalCode,
                originCity: shipFrom.city,
                originState: shipFrom.state,
                destinationCountry: shipTo.country,
                destinationPostalCode: shipTo.postalCode,
                destinationCity: shipTo.city,
                destinationState: shipTo.state,
                originRemoteAreaUSD: originRemote,
                destinationRemoteAreaUSD: destRemote,
                totalWeightKg: totalWeight,
                declaredValue: declaredValue,
                packages: packages.map(p => ({
                    ...p,
                    additionalHandling: !!p.additionalHandling
                })) // For dimensions and surcharges
            });
            if (res.data.success) {
                setQuote(res.data);
                setCardState('rated');
            } else {
                setError(res.data.message || 'Rate lookup failed');
                setCardState('idle');
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Rate lookup failed');
            setCardState('idle');
        }
    };

    const handleReceivePayment = () => {
        setCardState('paid');
    };

    const handleCreateLabel = async () => {
        setError('');
        setCardState('creating');
        try {
            // If cash, set transaction id to 'cash' so receipt shows it
            const txIdToSend = String(paymentMethod).toLowerCase() === 'cash' ? 'cash' : (transactionIdState || '');

            const payload = {
                accountNumber: account,
                serviceType: productType,
                shipFrom,
                shipTo,
                packages: packages.map(p => ({
                    ...p,
                    weightUnit: 'KGS',
                    dimUnit: 'CM',
                    // Explicit actual weight for label printing
                    actualWeight: parseFloat(p.weight) || 0,
                    billableWeight: (typeof p.billableWeight !== 'undefined') ? parseFloat(p.billableWeight) : undefined,
                    additionalHandling: !!p.additionalHandling
                })),
                generalDescription,
                reference1,
                reference2,
                user: currentUser,
                companySlug,
                paymentMethod,
                originNotify,
                destinationNotify,
                payment: { method: paymentMethod, transactionId: txIdToSend },
                gstOption,
                chargesSnapshot: {
                    ...quote?.charges,
                    gstUSD: gstCalc.usd,
                    gstPKR: gstCalc.pkr,
                    grandTotalUSD,
                    grandTotalPKR: grandTotalUSD * exc,
                    exchangeRate: exc,
                    rateUsed: exc
                },
                createdAt: new Date().toISOString()
            };
            const res = await axios.post(`${API}/fc-label/create`, payload);
            if (res.data.success) {
                setLabelResult(res.data);
                setCardState('done');
                if (onLabelCreated) onLabelCreated(res.data);
            } else {
                setError(res.data.message || 'Failed to create FC label.');
                setCardState('paid');
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to create FC label.');
            setCardState('paid');
        }
    };

    const handleReset = () => {
        setCardState('idle');
        setQuote(null);
        setError('');
        setLabelResult(null);
    };

    return (
        <div
            style={{
                border: isSelected ? '2px solid #6366f1' : '2px solid #e2e8f0',
                borderRadius: 16,
                background: isSelected ? '#fafbff' : '#fff',
                boxShadow: isSelected
                    ? '0 4px 24px rgba(99,102,241,0.15)'
                    : '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.2s',
                overflow: 'hidden',
                marginBottom: 16
            }}
        >
            {/* Card Header */}
            <div
                onClick={() => onSelect(account)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 18px', cursor: 'pointer',
                    background: isSelected
                        ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                        : 'linear-gradient(135deg, #f8fafc, #f1f5f9)'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>✈️</span>
                    <div>
                        <div style={{
                            fontFamily: 'monospace', fontWeight: 700, fontSize: 14,
                            color: isSelected ? '#fff' : '#1e293b'
                        }}>{account}</div>
                        <div style={{ fontSize: 11, color: isSelected ? '#c7d2fe' : '#94a3b8' }}>
                            Import Account · {productType}
                            {totalWeight > 0 ? ` · ${fmt2(totalWeight)} kg` : ''}
                            {shipFrom.country ? ` · From ${shipFrom.country}` : ''}
                        </div>
                    </div>
                </div>
                <div style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
                    background: isSelected ? 'rgba(255,255,255,0.2)' : '#e0e7ff',
                    color: isSelected ? '#fff' : '#4338ca'
                }}>
                    {isSelected ? '● Selected' : 'Click to Select'}
                </div>
            </div>

            {/* Card Body — only visible when selected */}
            {isSelected && (
                <div style={{ padding: '16px 18px' }}>

                    {/* Error Banner */}
                    {error && (
                        <div style={{
                            background: '#fef2f2', border: '1px solid #fecaca',
                            borderRadius: 8, padding: '8px 12px', marginBottom: 12,
                            fontSize: 12, color: '#dc2626', fontWeight: 600
                        }}>
                            ⚠️ {error}
                        </div>
                    )}

                    {/* STEP 1: GET RATE */}
                    {(cardState === 'idle' || cardState === 'rating') && (
                        <button
                            onClick={handleGetRate}
                            disabled={cardState === 'rating' || !formReady}
                            style={{
                                width: '100%', padding: '10px 0', borderRadius: 10,
                                background: formReady
                                    ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                                    : '#e2e8f0',
                                color: formReady ? '#fff' : '#94a3b8',
                                border: 'none', cursor: formReady ? 'pointer' : 'not-allowed',
                                fontWeight: 700, fontSize: 13, display: 'flex',
                                alignItems: 'center', justifyContent: 'center', gap: 8
                            }}
                        >
                            {cardState === 'rating'
                                ? <><span className="fc-spinner" />Fetching Rate…</>
                                : '💰 Get Rate Quote'}
                        </button>
                    )}

                    {/* STEP 2: RATE SHOWN */}
                    {(cardState === 'rated' || cardState === 'paid' || cardState === 'creating' || cardState === 'done') && quote && (
                        <div>
                            {/* UPS Rating Errors - Hard Block */}
                            {quote.upsRatingError ? (
                                <div>
                                    <div style={{
                                        background: '#fef2f2', border: '1px solid #fecaca',
                                        borderRadius: 10, padding: '12px 16px', marginBottom: 16,
                                        color: '#b91c1c'
                                    }}>
                                        <div style={{ fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                            <span>❌</span> UPS API Error
                                        </div>
                                        <div style={{ fontSize: 13, background: 'rgba(255,255,255,0.6)', padding: '8px', borderRadius: 6, fontFamily: 'monospace' }}>
                                            {quote.upsRatingError}
                                        </div>
                                        <div style={{ fontSize: 11, fontWeight: 600, marginTop: 8, color: '#991b1b' }}>
                                            Rates cannot be generated. Please fix the address or package details and try again.
                                        </div>
                                    </div>
                                    <button onClick={handleReset} style={{
                                        width: '100%', padding: '10px 14px', borderRadius: 10,
                                        background: '#f1f5f9', color: '#64748b',
                                        border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12
                                    }}>
                                        ↺ Back to Rate Quote
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* API Alerts */}
                                    {quote.alerts && quote.alerts.length > 0 && (
                                        <div style={{
                                            background: '#fffbeb', border: '1px solid #fcd34d',
                                            borderRadius: 10, padding: '10px 14px', marginBottom: 12,
                                            fontSize: 12, color: '#b45309'
                                        }}>
                                            <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ UPS Rating Alerts:</div>
                                            <ul style={{ margin: 0, paddingLeft: 22, listStyleType: 'disc' }}>
                                                {quote.alerts.map((a, i) => (
                                                    <li key={i} style={{ marginBottom: 4 }}>{a.Description || a.message || a.Code}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Charges Breakdown */}
                                    <div style={{
                                        background: '#f8fafc', borderRadius: 10,
                                        padding: '12px 14px', marginBottom: 12
                                    }}>
                                        <div style={{
                                            fontWeight: 700, fontSize: 11, color: '#6366f1',
                                            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8
                                        }}>
                                            📊 Charge Breakdown — {getCountryName(shipFrom.country)} → PK
                                        </div>

                                        {toRow('Freight (Import Tariff Sheet)', quote.charges.freightUSD)}
                                        {toRow('SBC (Security Screening Charge)', quote.charges.sbcUSD)}
                                        {toRow(`Fuel Surcharge (${(quote.fuelSurchargePct * 100).toFixed(2)}%)`, quote.charges.fuelSurchargeUSD)}
                                        {toRow('Print Label Charges (UPS)', quote.charges.upsPrintLabelUSD)}
                                        {toRow('Additional Handling (UPS)', quote.charges.upsAdditionalHandlingUSD)}
                                        {toRow('Peak Season Surcharge - LP (UPS Code 431)', quote.charges.upsPeakSeasonLargePackageUSD)}
                                        {toRow('Peak Season Surcharge - AH (UPS Code 432)', quote.charges.upsPeakSeasonAdditionalHandlingUSD)}
                                        {toRow('Origin Remote Area Charges', quote.charges.originRemoteAreaUSD)}
                                        {toRow('Destination Remote Area Charges', quote.charges.destinationRemoteAreaUSD)}
                                        {toRow('International Processing (IPF) (UPS)', quote.charges.upsIPFUSD)}
                                        {toRow('Large Package Surcharge (UPS)', quote.charges.upsLargePackageSurchargeUSD)}
                                        {toRow('Insurance / Declared Value', quote.charges.upsInsuranceUSD)}
                                        {quote.charges.peakChargesUSD > 0 && toRow('Peak Period Charges', quote.charges.peakChargesUSD)}

                                        <div style={{ borderTop: '2px solid #e2e8f0', marginTop: 8, paddingTop: 8 }}>
                                            <div style={{
                                                display: 'flex', justifyContent: 'space-between',
                                                fontSize: 12, fontWeight: 700, marginBottom: 8
                                            }}>
                                                <span style={{ color: '#475569' }}>Subtotal (before GST)</span>
                                                <span style={{ color: '#1e293b' }}>
                                                    ${fmt2(quote.charges.subtotalUSD)}
                                                    <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>
                                                        (PKR {fmt2(quote.charges.subtotalUSD * exc)})
                                                    </span>
                                                </span>
                                            </div>

                                            {/* GST Selector */}
                                            <div style={{ marginBottom: 8 }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: '#4a5568', marginBottom: 6 }}>
                                                    GST / Tax Rate:
                                                </div>
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    {GST_OPTIONS.map(opt => (
                                                        <button
                                                            key={opt.value}
                                                            onClick={() => setGstOption(opt.value)}
                                                            disabled={cardState === 'paid' || cardState === 'creating' || cardState === 'done'}
                                                            style={{
                                                                padding: '4px 10px', borderRadius: 6, border: 'none',
                                                                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                                background: gstOption === opt.value ? '#6366f1' : '#e0e7ff',
                                                                color: gstOption === opt.value ? '#fff' : '#4338ca'
                                                            }}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {toRow('GST', gstCalc.usd, gstCalc.pkr)}

                                            <div style={{
                                                display: 'flex', justifyContent: 'space-between',
                                                fontSize: 14, fontWeight: 800, marginTop: 8, paddingTop: 8,
                                                borderTop: '2px solid #6366f1'
                                            }}>
                                                <span style={{ color: '#4338ca' }}>Grand Total</span>
                                                <span style={{ color: '#1e293b' }}>
                                                    ${fmt2(grandTotalUSD)}
                                                    <span style={{
                                                        background: '#6366f1', color: '#fff',
                                                        borderRadius: 6, padding: '2px 8px',
                                                        fontSize: 11, marginLeft: 8
                                                    }}>
                                                        PKR {fmt2(grandTotalUSD * exc)}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>

                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>
                                            Rate: 1 USD = PKR {fmt2(exc)} · Weight tier: {fmt2(quote.matchedWeight)} kg
                                        </div>
                                    </div>

                                    {/* STEP 2 buttons: Receive Payment */}
                                    {cardState === 'rated' && (
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button
                                                onClick={handleReceivePayment}
                                                style={{
                                                    flex: 1, padding: '10px 0', borderRadius: 10,
                                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                                    color: '#fff', border: 'none', cursor: 'pointer',
                                                    fontWeight: 700, fontSize: 13
                                                }}
                                            >
                                                💳 Receive Payment
                                            </button>
                                            <button onClick={handleReset} style={{
                                                padding: '10px 14px', borderRadius: 10,
                                                background: '#f1f5f9', color: '#64748b',
                                                border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12
                                            }}>
                                                ↺ Reset
                                            </button>
                                        </div>
                                    )}

                                    {/* STEP 3: Payment method + Create Label */}
                                    {(cardState === 'paid' || cardState === 'creating') && (
                                        <div>
                                            <div style={{
                                                background: '#ecfdf5', border: '1px solid #6ee7b7',
                                                borderRadius: 8, padding: '8px 12px', marginBottom: 10,
                                                fontSize: 12, color: '#065f46', fontWeight: 600
                                            }}>
                                                ✅ Payment Received — select method &amp; create label
                                            </div>
                                            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                                                {['cash', 'online'].map(m => (
                                                    <button
                                                        key={m}
                                                        onClick={() => { setPaymentMethod(m); setTransactionIdState(m === 'cash' ? 'cash' : ''); }}
                                                        style={{
                                                            flex: 1, padding: '8px 0', borderRadius: 8,
                                                            border: 'none', cursor: 'pointer',
                                                            fontWeight: 700, fontSize: 12,
                                                            background: paymentMethod === m ? '#1e293b' : '#f1f5f9',
                                                            color: paymentMethod === m ? '#fff' : '#64748b'
                                                        }}
                                                    >
                                                        {m === 'cash' ? '💵 Cash' : '🌐 Online'}
                                                    </button>
                                                ))}
                                            </div>
                                            {paymentMethod === 'online' && (
                                                <div style={{ marginBottom: 10 }}>
                                                    <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 6 }}>Transaction ID</label>
                                                    <input
                                                        value={transactionIdState}
                                                        onChange={e => setTransactionIdState(e.target.value)}
                                                        placeholder="Enter transaction/reference ID"
                                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                                                    />
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button
                                                    onClick={handleCreateLabel}
                                                    disabled={cardState === 'creating'}
                                                    style={{
                                                        flex: 1, padding: '10px 0', borderRadius: 10,
                                                        background: cardState === 'creating'
                                                            ? '#e2e8f0'
                                                            : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                                        color: cardState === 'creating' ? '#94a3b8' : '#fff',
                                                        border: 'none',
                                                        cursor: cardState === 'creating' ? 'not-allowed' : 'pointer',
                                                        fontWeight: 700, fontSize: 13,
                                                        display: 'flex', alignItems: 'center',
                                                        justifyContent: 'center', gap: 8
                                                    }}
                                                >
                                                    {cardState === 'creating'
                                                        ? <><span className="fc-spinner" />Creating…</>
                                                        : '🏷️ Create FC Label'}
                                                </button>
                                                <button onClick={handleReset} style={{
                                                    padding: '10px 14px', borderRadius: 10,
                                                    background: '#f1f5f9', color: '#64748b',
                                                    border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12
                                                }}>
                                                    ↺
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* STEP 4: Done — Show label info */}
                                    {cardState === 'done' && labelResult && (
                                        <div style={{
                                            background: '#ecfdf5', border: '2px solid #10b981',
                                            borderRadius: 10, padding: '12px 14px', textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: 18, marginBottom: 6 }}>✅</div>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#065f46' }}>
                                                Label Created!
                                            </div>
                                            <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#047857', margin: '4px 0' }}>
                                                {labelResult.trackingNumber}
                                            </div>
                                            <button onClick={handleReset} style={{
                                                marginTop: 8, padding: '6px 16px', borderRadius: 8,
                                                background: '#6366f1', color: '#fff',
                                                border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12
                                            }}>
                                                + New Quote
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const FCLabel = ({ onBack, userAccounts = [], importAccounts: userImportAccounts = [], isSuperUser = false, currentUser = '', companySlug = '' }) => {

    /* ── View state ── */
    const [view, setView] = useState('list'); // 'list' | 'create'

    /* ── List state ── */
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [historySearch, setHistorySearch] = useState('');
    const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
    const [totalHistoryCount, setTotalHistoryCount] = useState(0);
    const [selectedShipments, setSelectedShipments] = useState(new Set());
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterAccount, setFilterAccount] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterTracking, setFilterTracking] = useState('');
    const [filterRecipient, setFilterRecipient] = useState('');
    const [filterCreatedBy, setFilterCreatedBy] = useState('');
    const [filterMinCost, setFilterMinCost] = useState('');
    const [filterMaxCost, setFilterMaxCost] = useState('');
    const [showDetails, setShowDetails] = useState(null);

    /* ── Form state ── */
    const [productType, setProductType] = useState('NON-DOCUMENTS');
    const [shipFrom, setShipFrom] = useState({ ...EMPTY_ADDR });
    const [shipTo, setShipTo] = useState({ ...EMPTY_DEST });
    const [originNotify, setOriginNotify] = useState({ enabled: false, ship: true, exception: true, delivery: true });
    const [destinationNotify, setDestinationNotify] = useState({ enabled: false, ship: true, exception: true, delivery: true });
    const [packages, setPackages] = useState([{ ...EMPTY_PKG }]);
    const [generalDescription, setGeneralDescription] = useState('');
    const [reference1, setReference1] = useState('');
    const [reference2, setReference2] = useState('');
    const [declaredValue, setDeclaredValue] = useState('');

    /* ── Result / UI state ── */
    const [labelResult, setLabelResult] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [importAccounts, setImportAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [createPickup, setCreatePickup] = useState(false);
    const [creatingPickup, setCreatingPickup] = useState(false);

    /* ── Stable onChange handlers ── */
    const handleShipFromChange = useCallback((field, val) => {
        setShipFrom(prev => ({ ...prev, [field]: val }));
    }, []);

    const handleShipToChange = useCallback((field, val) => {
        setShipTo(prev => ({ ...prev, [field]: val }));
    }, []);

    const handleSwitchAddresses = useCallback(() => {
        const tempFrom = { ...shipFrom };
        const tempTo = { ...shipTo };
        setShipFrom({ ...tempTo, country: tempTo.country || 'PK' });
        setShipTo({ ...tempFrom, country: tempFrom.country || 'US' });
    }, [shipFrom, shipTo]);

    /* ──────── Fetch import accounts (from import_tariffs) ──────── */
    const fetchImportAccounts = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/settings/import-tariffs`);
            if (res.data.success) {
                let accts = [...new Set((res.data.tariffs || []).map(t => t.account))].filter(Boolean);
                if (!isSuperUser && userImportAccounts && userImportAccounts.length > 0 && !userImportAccounts.includes('all')) {
                    const allowedSet = new Set(userImportAccounts.map(a => String(a).trim()));
                    accts = accts.filter(a => allowedSet.has(String(a).trim()));
                }
                setImportAccounts(accts);
                if (accts.length === 1) setSelectedAccount(accts[0]);
            }
        } catch (err) {
            console.error('[FCLabel] Failed to fetch import accounts', err);
        }
    }, [isSuperUser, userImportAccounts]);

    /* ──────── Fetch history ──────── */
    const fetchHistory = useCallback(async (page = 1, append = false) => {
        if (page === 1) {
            setLoadingHistory(true);
            setHistoryError('');
        } else {
            setLoadingMore(true);
        }
        try {
            const res = await axios.get(`${API}/fc-label/list?page=${page}&limit=10`, { timeout: 5000 });
            if (res.data.success) {
                const newLabels = res.data.labels || [];
                const total = res.data.totalCount || 0;

                if (append) {
                    setHistory(prev => [...prev, ...newLabels]);
                } else {
                    setHistory(newLabels);
                    setCurrentHistoryPage(1);
                }
                setTotalHistoryCount(total);
                setHistoryError('');
            } else {
                if (!append) setHistory([]);
                setHistoryError(res.data.message || 'Failed to load history');
            }
        } catch (err) {
            console.error('[FCLabel] Failed to fetch history', err);
            if (!append) setHistory([]);
            // Don't show error if database is just unavailable in local dev
            if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
                setHistoryError('Server not available - showing empty list');
            } else if (err.response?.status === 401) {
                setHistoryError('Please log in to view shipment history');
            } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
                setHistoryError('Request timed out - please try again');
            } else {
                setHistoryError(err.response?.data?.message || err.message || 'Unable to load shipment history.');
            }
        } finally {
            if (page === 1) setLoadingHistory(false);
            if (page > 1) setLoadingMore(false);
        }
    }, []);

    const loadMoreHistory = useCallback(async () => {
        const nextPage = currentHistoryPage + 1;
        setCurrentHistoryPage(nextPage);
        await fetchHistory(nextPage, true);
    }, [currentHistoryPage, fetchHistory]);

    useEffect(() => {
        fetchImportAccounts();
        fetchHistory(1, false);
    }, [fetchImportAccounts, fetchHistory]);

    /* ──────── Package helpers ──────── */
    const addPkg = () => setPackages(prev => [...prev, { ...EMPTY_PKG }]);
    const removePkg = (i) => setPackages(prev => prev.filter((_, idx) => idx !== i));
    const updatePkg = (i, field, val) =>
        setPackages(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

    /* ──────── Form readiness check ──────── */
    const totalWeight = packages.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
    const formReady = !!(
        shipFrom.country &&
        shipFrom.name &&
        shipFrom.address1 &&
        shipFrom.city &&
        shipTo.name &&
        shipTo.address1 &&
        shipTo.city &&
        shipTo.country &&
        generalDescription.trim() &&
        totalWeight > 0
    );

    /* ──────── Reset form ──────── */
    const resetForm = () => {
        setShipFrom({ ...EMPTY_ADDR });
        setShipTo({ ...EMPTY_DEST });
        setPackages([{ ...EMPTY_PKG }]);
        setGeneralDescription('');
        setReference1('');
        setReference2('');
        setDeclaredValue('0');
        setProductType('NON-DOCUMENTS');
        setSelectedAccount('');
        setCreatePickup(false);
    };

    /* ──────── Create Pickup ──────── */
    const handleCreatePickup = async (labelData) => {
        if (!createPickup) return;
        setCreatingPickup(true);
        try {
            console.log('[FCLabel] Creating pickup for tracking:', labelData.trackingNumber);
            console.log('[FCLabel] Auth token present:', !!localStorage.getItem('authToken'));
            console.log('[FCLabel] Axios default auth header:', axios.defaults.headers.common['Authorization']?.substring(0, 20) + '...');

            const pickupPayload = {
                trackingNumber: labelData.trackingNumber,
                shipFrom: shipFrom,
                shipTo: shipTo,
                packages: packages,
                accountNumber: labelData.accountNumber,
                serviceType: productType,
                user: currentUser,
                companySlug: companySlug,
                generalDescription: generalDescription
            };

            console.log('[FCLabel] Sending pickup payload:', JSON.stringify(pickupPayload, null, 2));
            const res = await axios.post(`${API}/fc-label/create-pickup`, pickupPayload, { timeout: 10000 });

            console.log('[FCLabel] Pickup response status:', res.status);
            console.log('[FCLabel] Pickup response data:', res.data);

            if (res.data.success) {
                console.log('[FCLabel] ✅ Pickup created successfully:', res.data);
                alert('✅ Pickup created successfully!\nPickup ID: ' + (res.data.pickupId || 'N/A'));
            } else {
                console.error('[FCLabel] ❌ Pickup failed:', res.data.message);
                alert('❌ Pickup failed: ' + res.data.message);
            }
        } catch (err) {
            console.error('[FCLabel] ❌ Pickup creation error:', err.message);
            console.error('[FCLabel] Error response:', err.response?.data);
            console.error('[FCLabel] Error status:', err.response?.status);
            console.error('[FCLabel] Full error:', err);
            alert('❌ Pickup Error: ' + (err.response?.data?.message || err.message));
        } finally {
            setCreatingPickup(false);
        }
    };

    /* ──────── Print / Download ──────── */
    const printLabel = (labels) => {
        if (!labels?.length) return;
        const win = window.open('');
        win.document.write(`<!DOCTYPE html><html><head><title>FC Label</title><style>
@page{size:4in 6in;margin:0}body{margin:0;padding:0}
.page{page-break-after:always;display:flex;justify-content:center;padding:5mm}
img{max-width:100%;height:auto}
</style></head><body>
${labels.map(l => `<div class="page"><img src="data:image/gif;base64,${l}"/></div>`).join('')}
</body></html>`);
        win.document.close();
        win.print();
    };

    const downloadLabel = (labels, tn) => {
        if (!labels?.length) return;
        labels.forEach((l, i) => {
            const a = document.createElement('a');
            a.href = `data:image/gif;base64,${l}`;
            a.download = `FC-${tn}${labels.length > 1 ? `-${i + 1}` : ''}.gif`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        });
    };

    const downloadReceipt = (shipmentData) => {
        try {
            openReceiptWindow({
                receiptNo: shipmentData.invoiceNo || shipmentData.commodityInvoiceNo || '',
                invoiceNo: shipmentData.invoiceNo,
                trackingNumber: shipmentData.trackingNumber,
                amountPKR: shipmentData.totalChargesPKR || shipmentData.chargesSnapshot?.grandTotalPKR || shipmentData.totalCharges || 0,
                receiverName: shipmentData.shipTo?.name || shipmentData.receiverName || '',
                createdBy: shipmentData.createdBy || localStorage.getItem('currentUser') || '',
                paymentMethod: shipmentData.paymentMethod || '',
                transactionId: shipmentData.transactionId || '',
                drawnOn: shipmentData.drawnOn || '',
                accountNo: shipmentData.accountNumber || shipmentData.account || '',
                paymentDate: shipmentData.paymentDate || shipmentData.createdAt,
                receiptEnabled: shipmentData.receiptEnabled !== false,
                packages: shipmentData.packages || [],
                countryOfOrigin: shipmentData.shipTo?.country || shipmentData.destinationCountry || '',
                totalWeight: shipmentData.billingWeight || shipmentData.weight || 0
            });
        } catch (err) {
            console.error('[FCLabel] Receipt generation error:', err);
            alert('Failed to generate receipt: ' + err.message);
        }
    };

    const downloadCombined = (labels, shipmentData) => {
        if (!labels?.length) return;
        try {
            // For now, download receipt and label separately
            // In a production app, you'd combine them into a single PDF using a library like PDFKit
            alert('Downloading receipt and label...');
            openReceiptWindow({
                receiptNo: shipmentData.invoiceNo || shipmentData.commodityInvoiceNo || '',
                invoiceNo: shipmentData.invoiceNo,
                trackingNumber: shipmentData.trackingNumber,
                amountPKR: shipmentData.totalChargesPKR || shipmentData.chargesSnapshot?.grandTotalPKR || shipmentData.totalCharges || 0,
                receiverName: shipmentData.shipTo?.name || shipmentData.receiverName || '',
                createdBy: shipmentData.createdBy || localStorage.getItem('currentUser') || '',
                paymentMethod: shipmentData.paymentMethod || '',
                transactionId: shipmentData.transactionId || '',
                drawnOn: shipmentData.drawnOn || '',
                accountNo: shipmentData.accountNumber || shipmentData.account || '',
                paymentDate: shipmentData.paymentDate || shipmentData.createdAt,
                receiptEnabled: shipmentData.receiptEnabled !== false,
                packages: shipmentData.packages || [],
                countryOfOrigin: shipmentData.shipTo?.country || shipmentData.destinationCountry || '',
                totalWeight: shipmentData.billingWeight || shipmentData.weight || 0
            });

            // Also download the label
            setTimeout(() => {
                downloadLabel(labels, shipmentData.trackingNumber);
            }, 1000);
        } catch (err) {
            console.error('[FCLabel] Combined download error:', err);
            alert('Failed to generate combined download: ' + err.message);
        }
    };

    /* ──────── Void ──────── */
    const handleVoid = async (trackingNumber) => {
        if (!window.confirm(`Void FC shipment ${trackingNumber}?`)) return;
        try {
            await axios.delete(`${API}/fc-label/void/${trackingNumber}`);
            fetchHistory();
        } catch (e) {
            alert('Failed to void: ' + (e.response?.data?.message || e.message));
        }
    };

    /* ──────── View historical label ──────── */
    const handleViewLabel = async (item) => {
        // Show loading state immediately
        setLabelResult({ trackingNumber: item.trackingNumber, loading: true });
        setShowResult(true);
        try {
            // Fetch full document (list endpoint sanitizes labelImage to "(available)" placeholder)
            const res = await axios.get(`${API}/fc-label/get/${encodeURIComponent(item.trackingNumber)}`);
            if (res.data.success) {
                const full = res.data.label || res.data;
                setLabelResult({
                    trackingNumber: full.trackingNumber || item.trackingNumber,
                    labels: full.labels || res.data.labels || [],
                    labelImage: full.labelImage || res.data.labelImage,
                    // carry over metadata needed by receipt / combined download
                    invoiceNo: full.invoiceNo,
                    totalChargesPKR: full.totalChargesPKR,
                    chargesSnapshot: full.chargesSnapshot,
                    shipTo: full.shipTo,
                    shipFrom: full.shipFrom,
                    createdBy: full.createdBy,
                    paymentMethod: full.paymentMethod,
                    transactionId: full.transactionId,
                    accountNumber: full.accountNumber,
                    packages: full.packages,
                    billingWeight: full.billingWeight,
                    createdAt: full.createdAt,
                });
            } else {
                setLabelResult({
                    trackingNumber: item.trackingNumber,
                    error: res.data.message || 'Label not found',
                });
            }
        } catch (err) {
            console.error('[FCLabel] handleViewLabel fetch error:', err);
            setLabelResult({
                trackingNumber: item.trackingNumber,
                error: err.response?.data?.message || err.message || 'Failed to load label',
            });
        }
    };

    /* ──────── Filter ──────── */
    const filtered = history.filter(h => {
        // Search
        if (historySearch) {
            const s = historySearch.toLowerCase();
            const matchesSearch = (h.trackingNumber || '').toLowerCase().includes(s)
                || (h.shipFrom?.name || '').toLowerCase().includes(s)
                || (h.shipTo?.name || '').toLowerCase().includes(s)
                || (h.shipFrom?.city || '').toLowerCase().includes(s);
            if (!matchesSearch) return false;
        }

        // Tracking # filter
        if (filterTracking) {
            const s = filterTracking.toLowerCase();
            if (!(h.trackingNumber || '').toLowerCase().includes(s)) return false;
        }

        // Recipient filter
        if (filterRecipient) {
            const s = filterRecipient.toLowerCase();
            if (!((h.shipTo?.name || '').toLowerCase().includes(s) || (h.shipTo?.city || '').toLowerCase().includes(s))) return false;
        }

        // Created By filter
        if (filterCreatedBy) {
            const s = filterCreatedBy.toLowerCase();
            if (!((h.createdBy || '').toLowerCase().includes(s))) return false;
        }

        // Min Cost filter (USD)
        if (filterMinCost) {
            const minCost = parseFloat(filterMinCost);
            const itemCost = parseFloat(h.chargesSnapshot?.grandTotalUSD || 0);
            if (itemCost < minCost) return false;
        }

        // Max Cost filter (USD)
        if (filterMaxCost) {
            const maxCost = parseFloat(filterMaxCost);
            const itemCost = parseFloat(h.chargesSnapshot?.grandTotalUSD || 0);
            if (itemCost > maxCost) return false;
        }

        // Date
        const date = new Date(h.createdAt || h.shipmentDate);
        if (filterDateFrom && date < new Date(filterDateFrom)) return false;
        if (filterDateTo) {
            const toDate = new Date(filterDateTo);
            toDate.setHours(23, 59, 59, 999);
            if (date > toDate) return false;
        }

        // Status
        if (filterStatus && h.status !== filterStatus) return false;

        // Account
        if (filterAccount && h.accountNumber !== filterAccount) return false;

        // Type
        if (filterType && h.serviceType !== filterType) return false;

        return true;
    });

    const stats = {
        total: history.length,
        active: history.filter(h => h.status !== 'voided').length,
        voided: history.filter(h => h.status === 'voided').length,
        docs: history.filter(h => h.serviceType === 'DOCUMENTS').length
    };

    /* ──────── Bulk Action Handlers ──────── */
    const toggleSelectAll = useCallback(() => {
        if (selectedShipments.size === filtered.length) {
            setSelectedShipments(new Set());
        } else {
            setSelectedShipments(new Set(filtered.map((_, idx) => idx)));
        }
    }, [filtered, selectedShipments.size]);

    const toggleSelectShipment = useCallback((idx) => {
        const newSelected = new Set(selectedShipments);
        if (newSelected.has(idx)) {
            newSelected.delete(idx);
        } else {
            newSelected.add(idx);
        }
        setSelectedShipments(newSelected);
    }, [selectedShipments]);

    const getSelectedItems = useCallback(() => {
        return Array.from(selectedShipments).map(idx => filtered[idx]).filter(Boolean);
    }, [selectedShipments, filtered]);

    const handleBulkLabel = useCallback(async () => {
        const items = getSelectedItems();
        if (items.length !== 1) { alert('Please select exactly 1 shipment'); return; }
        const item = items[0];
        // Show loading state, then fetch full document (list has placeholder images)
        setLabelResult({ trackingNumber: item.trackingNumber, loading: true });
        setShowResult(true);
        try {
            console.log('[FCLabel] Label action for:', item.trackingNumber);
            const res = await axios.get(`${API}/fc-label/get/${encodeURIComponent(item.trackingNumber)}`);
            if (res.data.success) {
                const full = res.data.label || res.data;
                setLabelResult({
                    trackingNumber: full.trackingNumber || item.trackingNumber,
                    labels: full.labels || res.data.labels || [],
                    labelImage: full.labelImage || res.data.labelImage,
                    invoiceNo: full.invoiceNo,
                    totalChargesPKR: full.totalChargesPKR,
                    chargesSnapshot: full.chargesSnapshot,
                    shipTo: full.shipTo,
                    shipFrom: full.shipFrom,
                    createdBy: full.createdBy,
                    paymentMethod: full.paymentMethod,
                    transactionId: full.transactionId,
                    accountNumber: full.accountNumber,
                    packages: full.packages,
                    billingWeight: full.billingWeight,
                    createdAt: full.createdAt,
                });
            } else {
                setLabelResult({ trackingNumber: item.trackingNumber, error: res.data.message || 'Label not found' });
            }
        } catch (err) {
            console.error('[FCLabel] Label error:', err);
            setLabelResult({ trackingNumber: item.trackingNumber, error: err.response?.data?.message || err.message || 'Failed to load label' });
        }
    }, [getSelectedItems]);

    const handleBulkBilling = useCallback(async () => {
        const items = getSelectedItems();
        if (items.length !== 1) { alert('Please select exactly 1 shipment'); return; }
        try {
            console.log('[FCLabel] Billing action for:', items[0].trackingNumber);
            const item = items[0];
            if (!item.chargesSnapshot) {
                alert('Billing information not available for this shipment');
                return;
            }
            const charges = item.chargesSnapshot;
            const billingText = `BILLING BREAKDOWN\n─────────────────\nShipment: ${item.trackingNumber}\nAccount: ${item.accountNumber}\n\nSubtotal (USD):     $${(charges.subtotalUSD || 0).toFixed(2)}\nFuel Surcharge:     $${(charges.fuelSurchargeUSD || 0).toFixed(2)}\nTax/GST:            $${(charges.gstUSD || 0).toFixed(2)}\n─────────────────\nTOTAL (USD):        $${(charges.grandTotalUSD || 0).toFixed(2)}\nExchange Rate:      1 USD = ${(charges.exchangeRate || 280.5).toFixed(2)} PKR\nTotal (PKR):        PKR ${((charges.grandTotalUSD || 0) * (charges.exchangeRate || 280.5)).toFixed(2)}`;
            alert(billingText);
        } catch (err) {
            console.error('[FCLabel] Billing error:', err);
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
    }, [getSelectedItems]);

    const handleBulkReceipt = useCallback(async () => {
        const items = getSelectedItems();
        if (items.length !== 1) { alert('Please select exactly 1 shipment'); return; }
        try {
            console.log('[FCLabel] Receipt download for:', items[0].trackingNumber);
            downloadReceipt(items[0]);
        } catch (err) {
            console.error('[FCLabel] Receipt error:', err);
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
    }, [getSelectedItems]);

    const handleBulkCommercial = useCallback(async () => {
        const items = getSelectedItems();
        if (!items.length) { alert('Please select shipments'); return; }
        try {
            const trackingNumbers = items.map(item => item.trackingNumber);
            console.log('[FCLabel] Bulk Commercial Invoice action:', trackingNumbers);
            const res = await axios.post(`${API}/fc-label/bulk-commercial-invoices`, { trackingNumbers }, { timeout: 5000 });
            if (res.data.success) {
                alert(`Generated commercial invoices for ${items.length} shipment(s)`);
            } else {
                alert('Failed to generate commercial invoices: ' + res.data.message);
            }
        } catch (err) {
            console.error('[FCLabel] Bulk Commercial Invoice error:', err);
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
    }, [getSelectedItems]);

    const handleBulkDuplicate = useCallback(async () => {
        const items = getSelectedItems();
        if (items.length !== 1) { alert('Please select exactly 1 shipment'); return; }
        try {
            console.log('[FCLabel] Duplicate action for:', items[0].trackingNumber);
            const item = items[0];
            setShipFrom(item.shipFrom || EMPTY_ADDR);
            setShipTo(item.shipTo || EMPTY_DEST);
            setPackages(item.packages || [EMPTY_PKG]);
            setGeneralDescription(item.generalDescription || '');
            setReference1(item.reference1 || '');
            setReference2(item.reference2 || '');
            setDeclaredValue(item.declaredValue || '0');
            setSelectedAccount(item.accountNumber || '');
            setProductType(item.serviceType || 'NON-DOCUMENTS');
            setView('create');
        } catch (err) {
            console.error('[FCLabel] Duplicate error:', err);
            alert('Error: ' + err.message);
        }
    }, [getSelectedItems]);

    const handleBulkProcess = useCallback(async () => {
        const items = getSelectedItems();
        if (!items.length) { alert('Please select shipments'); return; }
        try {
            const trackingNumbers = items.map(item => item.trackingNumber);
            console.log('[FCLabel] Bulk Process action:', trackingNumbers);
            const res = await axios.post(`${API}/fc-label/bulk-process`, { trackingNumbers }, { timeout: 5000 });
            if (res.data.success) {
                alert(`Processed ${items.length} shipment(s)`);
                fetchHistory(1, false);
                setSelectedShipments(new Set());
            } else {
                alert('Failed to process shipments: ' + res.data.message);
            }
        } catch (err) {
            console.error('[FCLabel] Bulk Process error:', err);
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
    }, [getSelectedItems, fetchHistory]);

    const handleBulkVoid = useCallback(async () => {
        const items = getSelectedItems();
        if (!items.length) { alert('Please select shipments'); return; }
        if (!window.confirm(`Void ${items.length} shipment(s)? This cannot be undone.`)) return;

        try {
            const trackingNumbers = items.map(item => item.trackingNumber);
            console.log('[FCLabel] Bulk Void action:', trackingNumbers);
            const res = await axios.post(`${API}/fc-label/bulk-void`, { trackingNumbers }, { timeout: 5000 });
            if (res.data.success) {
                alert(`Voided ${items.length} shipment(s)`);
                fetchHistory(1, false);
                setSelectedShipments(new Set());
            } else {
                alert('Failed to void shipments: ' + res.data.message);
            }
        } catch (err) {
            console.error('[FCLabel] Bulk Void error:', err);
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
    }, [getSelectedItems, fetchHistory]);

    const handleBulkDelete = useCallback(async () => {
        const items = getSelectedItems();
        if (items.length !== 1) { alert('Please select exactly 1 shipment'); return; }
        if (items[0].status !== 'voided') { alert('Can only delete voided shipments'); return; }
        if (!window.confirm(`Delete voided shipment ${items[0].trackingNumber}? This cannot be undone.`)) return;

        try {
            console.log('[FCLabel] Delete action for:', items[0].trackingNumber);
            const res = await axios.post(`${API}/fc-label/bulk-delete`, { trackingNumbers: [items[0].trackingNumber] }, { timeout: 5000 });
            if (res.data.success) {
                alert(`Deleted shipment`);
                fetchHistory(1, false);
                setSelectedShipments(new Set());
            } else {
                alert('Failed to delete shipment: ' + res.data.message);
            }
        } catch (err) {
            console.error('[FCLabel] Delete error:', err);
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
    }, [getSelectedItems, fetchHistory]);

    const handleDownloadFiltered = useCallback(() => {
        if (!filtered.length) { alert('No shipments to download'); return; }
        console.log('[FCLabel] Download All Filtered', filtered.length, 'shipments');
        alert(`Downloading ${filtered.length} filtered shipments...`);
    }, [filtered]);

    const handleDownloadCSV = useCallback(() => {
        if (!filtered.length) { alert('No shipments to download'); return; }
        console.log('[FCLabel] Download CSV', filtered.length, 'shipments');
        alert(`Downloading CSV with ${filtered.length} shipments...`);
    }, [filtered]);

    /* ══════════════════════════════════════════════
       RENDER — LIST VIEW
    ══════════════════════════════════════════════ */
    if (view === 'list') return (
        <div className="fc-container">
            <div className="fc-header" style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Header with Title and Create Button */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #e2e8f0'
                }}>




                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1e293b' }}>
                        ✈️ Forward Collect Shipments
                    </h1>



                    <button
                        onClick={() => { setView('create'); resetForm(); }}
                        style={{
                            padding: '10px 20px', borderRadius: 10,
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: '#fff', border: 'none', cursor: 'pointer',
                            fontWeight: 700, fontSize: 13, display: 'flex',
                            alignItems: 'center', gap: 8, transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)'}
                        onMouseLeave={(e) => e.target.style.boxShadow = 'none'}
                    >
                        ➕ Create Shipment
                    </button>
                </div>

                {/* Filters */}
                <div className="fc-filters-section" style={{
                    display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20,
                    background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0',
                    width: '100%'
                }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <input
                            className="fc-search-input"
                            style={{ width: '100%' }}
                            placeholder="🔍 Search tracking, customer, city…"
                            value={historySearch}
                            onChange={e => setHistorySearch(e.target.value)}
                        />
                    </div>
                    <input type="date" className="fc-search-input" style={{ width: 140 }}
                        value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} title="Date From" />
                    <input type="date" className="fc-search-input" style={{ width: 140 }}
                        value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} title="Date To" />

                    <select className="fc-search-input" style={{ width: 130 }}
                        value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="voided">Voided</option>
                    </select>

                    <select className="fc-search-input" style={{ width: 130 }}
                        value={filterType} onChange={e => setFilterType(e.target.value)}>
                        <option value="">All Types</option>
                        {PRODUCT_TYPES.map(pt => <option key={pt.code} value={pt.code}>{pt.label}</option>)}
                    </select>

                    <input type="text" className="fc-search-input" style={{ width: 140 }}
                        placeholder="🔍 Tracking #" value={filterTracking} onChange={e => setFilterTracking(e.target.value)} />
                    <input type="text" className="fc-search-input" style={{ width: 140 }}
                        placeholder="👤 Recipient" value={filterRecipient} onChange={e => setFilterRecipient(e.target.value)} />
                    <input type="text" className="fc-search-input" style={{ width: 140 }}
                        placeholder="✍️ Created By" value={filterCreatedBy} onChange={e => setFilterCreatedBy(e.target.value)} />
                    <input type="number" className="fc-search-input" style={{ width: 120 }}
                        placeholder="Min Cost (PKR)" value={filterMinCost} onChange={e => setFilterMinCost(e.target.value)} />
                    <input type="number" className="fc-search-input" style={{ width: 120 }}
                        placeholder="Max Cost (PKR)" value={filterMaxCost} onChange={e => setFilterMaxCost(e.target.value)} />

                    <button className="fc-action-btn" onClick={() => {
                        setHistorySearch(''); setFilterDateFrom(''); setFilterDateTo('');
                        setFilterStatus(''); setFilterAccount(''); setFilterType('');
                        setFilterTracking(''); setFilterRecipient(''); setFilterCreatedBy('');
                        setFilterMinCost(''); setFilterMaxCost('');
                    }}>↺ Clear</button>
                </div>

                {/* Bulk Actions Section */}
                {filtered.length > 0 && (
                    <div style={{
                        display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center',
                        background: '#f0f9ff', padding: 16, borderRadius: 12, border: 'none',
                        width: '100%'
                    }}>
                        <span style={{ color: '#0369a1', fontWeight: 600, minWidth: 120 }}>
                            {selectedShipments.size} selected
                        </span>

                        <button
                            className="fc-action-btn"
                            onClick={handleBulkLabel}
                            disabled={selectedShipments.size !== 1}
                            title="View label (select 1 shipment)"
                            style={{ opacity: selectedShipments.size !== 1 ? 0.5 : 1, cursor: selectedShipments.size !== 1 ? 'not-allowed' : 'pointer' }}
                        >
                            📋 Label
                        </button>

                        <button
                            className="fc-action-btn"
                            onClick={handleBulkBilling}
                            disabled={selectedShipments.size !== 1}
                            title="Show billing (select 1 shipment)"
                            style={{ opacity: selectedShipments.size !== 1 ? 0.5 : 1, cursor: selectedShipments.size !== 1 ? 'not-allowed' : 'pointer' }}
                        >
                            💳 Billing
                        </button>

                        <button
                            className="fc-action-btn"
                            onClick={handleBulkReceipt}
                            disabled={selectedShipments.size !== 1}
                            title="Download receipt (select 1 shipment)"
                            style={{ opacity: selectedShipments.size !== 1 ? 0.5 : 1, cursor: selectedShipments.size !== 1 ? 'not-allowed' : 'pointer' }}
                        >
                            🧾 Receipt
                        </button>

                        <button
                            className="fc-action-btn"
                            onClick={handleBulkDuplicate}
                            disabled={selectedShipments.size !== 1}
                            title="Duplicate (select 1 shipment)"
                            style={{ opacity: selectedShipments.size !== 1 ? 0.5 : 1, cursor: selectedShipments.size !== 1 ? 'not-allowed' : 'pointer' }}
                        >
                            📋 Duplicate
                        </button>

                        <button
                            className="fc-action-btn"
                            onClick={handleBulkProcess}
                            disabled={selectedShipments.size === 0}
                            title="Process selected"
                            style={{ opacity: selectedShipments.size === 0 ? 0.5 : 1, cursor: selectedShipments.size === 0 ? 'not-allowed' : 'pointer' }}
                        >
                            ⚙️ Process Shipment
                        </button>

                        <button
                            className="fc-action-btn"
                            onClick={handleBulkVoid}
                            disabled={selectedShipments.size === 0}
                            title="Void selected"
                            style={{ color: '#f59e0b', opacity: selectedShipments.size === 0 ? 0.5 : 1, cursor: selectedShipments.size === 0 ? 'not-allowed' : 'pointer' }}
                        >
                            ⏹️ Void
                        </button>

                        <button
                            className="fc-action-btn"
                            onClick={handleBulkDelete}
                            disabled={selectedShipments.size !== 1 || (getSelectedItems()[0] && getSelectedItems()[0].status !== 'voided')}
                            title="Delete voided shipment (select 1 voided shipment)"
                            style={{ color: '#dc2626', opacity: selectedShipments.size !== 1 || (getSelectedItems()[0] && getSelectedItems()[0].status !== 'voided') ? 0.5 : 1, cursor: selectedShipments.size !== 1 || (getSelectedItems()[0] && getSelectedItems()[0].status !== 'voided') ? 'not-allowed' : 'pointer' }}
                        >
                            🗑️ Delete
                        </button>

                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                            <button className="fc-action-btn" onClick={handleDownloadFiltered} title="Download all filtered">📁 Download All Filtered</button>
                            <button className="fc-action-btn" onClick={handleDownloadCSV} title="Download as CSV">⬇️ Download CSV</button>
                        </div>
                    </div>
                )}

                {/* History table */}
                <div className="fc-table-section" style={{ width: '100%' }}>
                    <div className="fc-table-header">
                        <h2>Forward Collect Shipment History <span>{totalHistoryCount} total shipments</span></h2>
                    </div>

                    {historyError && (
                        <div style={{
                            background: '#fef3c7', border: '1px solid #fcd34d',
                            borderRadius: 10, padding: '12px 16px', marginBottom: 16,
                            color: '#92400e', fontWeight: 600, fontSize: 13
                        }}>
                            ⚠️ {historyError}
                            <button
                                onClick={() => fetchHistory()}
                                style={{
                                    marginLeft: 12, padding: '4px 10px', borderRadius: 6,
                                    background: '#fbbf24', color: '#fff', border: 'none',
                                    cursor: 'pointer', fontWeight: 600, fontSize: 11
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {loadingHistory ? (
                        <div className="fc-empty-state"><span className="fc-spinner" style={{ borderTopColor: '#6366f1' }} /> <p>Loading history…</p></div>
                    ) : filtered.length === 0 ? (
                        <div className="fc-empty-state">
                            <p>No FC labels match your filters.</p>
                            <small>Try adjusting your search criteria or create a new label.</small>
                        </div>
                    ) : (
                        <>
                            <div className="fc-table-wrap">
                                <table className="fc-table">
                                    <thead>
                                        <tr>
                                            <th style={{ padding: '1rem', textAlign: 'center', width: 50 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedShipments.size === filtered.length && filtered.length > 0}
                                                    onChange={toggleSelectAll}
                                                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                />
                                            </th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Created By</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Tracking #</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Ship From (USA)</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Ship To (PK)</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Charge (PKR)</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((item, idx) => (
                                            <tr key={item._id || idx}>
                                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedShipments.has(idx)}
                                                        onChange={() => toggleSelectShipment(idx)}
                                                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '1rem', fontSize: '0.85rem' }}>
                                                    {new Date(item.createdAt || item.shipmentDate).toLocaleDateString('en-GB')}
                                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                                        {new Date(item.createdAt || item.shipmentDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem', fontSize: '0.85rem' }}>
                                                    {item.createdBy || item.user || '—'}
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <span style={{ fontFamily: 'monospace', color: '#4338ca', fontWeight: 700 }}>
                                                        {item.trackingNumber || '—'}
                                                    </span>
                                                    <div style={{ fontSize: '10px', color: '#64748b' }}>Account: {item.accountNumber}</div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{item.shipFrom?.name || '—'}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{item.shipFrom?.city}, {item.shipFrom?.country}</div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{item.shipTo?.name || '—'}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{item.shipTo?.city}, PK</div>
                                                </td>
                                                <td style={{ padding: '1rem', fontWeight: 700, color: '#6366f1' }}>
                                                    PKR {fmt2((item.chargesSnapshot?.grandTotalUSD || 0) * (item.chargesSnapshot?.exchangeRate || 280.5))}
                                                    <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>
                                                        (${fmt2(item.chargesSnapshot?.grandTotalUSD || 0)} @ {fmt2(item.chargesSnapshot?.exchangeRate || 280.5)})
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <span className={`fc-status-badge ${item.status === 'voided' ? 'voided' : 'active'}`}>
                                                        {item.status === 'voided' ? '● Voided' : '● Active'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {history.length > 0 && history.length < totalHistoryCount && (
                                <div style={{ padding: '16px', textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>
                                    <button
                                        onClick={loadMoreHistory}
                                        disabled={loadingMore}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: 8,
                                            background: '#6366f1',
                                            color: '#fff',
                                            border: 'none',
                                            cursor: loadingMore ? 'not-allowed' : 'pointer',
                                            fontWeight: 600,
                                            fontSize: 13,
                                            opacity: loadingMore ? 0.6 : 1,
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {loadingMore ? '⏳ Loading...' : `📥 Load More (${history.length} / ${totalHistoryCount})`}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showDetails && (
                <div className="fc-form-overlay" onClick={() => setShowDetails(null)}>
                    <div className="fc-result-modal" style={{ maxWidth: 800 }} onClick={e => e.stopPropagation()}>
                        <div className="fc-result-head" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <h3 style={{ color: '#1e293b' }}>📦 FC Shipment Details</h3>
                            <button className="fc-close-btn" onClick={() => setShowDetails(null)}>✕</button>
                        </div>
                        <div style={{ padding: 24, maxHeight: '80vh', overflowY: 'auto' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24 }}>
                                {/* Left Column: Info */}
                                <div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Tracking # & Date</label>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: '#4338ca', fontFamily: 'monospace' }}>{showDetails.trackingNumber}</div>
                                        <div style={{ fontSize: 13, color: '#64748b' }}>{new Date(showDetails.createdAt || showDetails.shipmentDate).toLocaleString()}</div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Ship From (Origin)</label>
                                            <div style={{ fontWeight: 700 }}>{showDetails.shipFrom.name}</div>
                                            <div style={{ fontSize: 12 }}>{showDetails.shipFrom.address1}</div>
                                            <div style={{ fontSize: 12 }}>{showDetails.shipFrom.city}, {showDetails.shipFrom.country}</div>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Ship To (PK)</label>
                                            <div style={{ fontWeight: 700 }}>{showDetails.shipTo.name}</div>
                                            <div style={{ fontSize: 12 }}>{showDetails.shipTo.address1}</div>
                                            <div style={{ fontSize: 12 }}>{showDetails.shipTo.city}, PK</div>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Package Info</label>
                                        <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, marginTop: 4 }}>
                                            <span style={{ fontWeight: 700 }}>{showDetails.serviceType}</span> · {showDetails.packages.length} pkg(s)
                                            <span style={{ marginLeft: 8, color: '#64748b' }}>({showDetails.packages.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0).toFixed(2)} kg)</span>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Description: {showDetails.generalDescription}</div>
                                        </div>
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>System Info</label>
                                        <div style={{ fontSize: 12 }}>Created By: <b>{showDetails.user || showDetails.createdBy}</b></div>
                                        <div style={{ fontSize: 12 }}>Account: <b>{showDetails.accountNumber}</b></div>
                                        {showDetails.reference1 && <div style={{ fontSize: 12 }}>Reference: <b>{showDetails.reference1}</b></div>}
                                    </div>
                                </div>

                                {/* Right Column: Charges */}
                                <div style={{ background: '#f8fafc', padding: 20, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                                    <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>📊 Charges Summary</h4>
                                    {showDetails.chargesSnapshot ? (
                                        <>
                                            {[
                                                { l: 'Freight Charge', v: showDetails.chargesSnapshot.freightUSD },
                                                { l: 'SBC/Security', v: showDetails.chargesSnapshot.sbcUSD },
                                                { l: 'Fuel Surcharge', v: showDetails.chargesSnapshot.fuelSurchargeUSD },
                                                { l: 'Print Label Charges (UPS)', v: showDetails.chargesSnapshot.upsPrintLabelUSD },
                                                { l: 'UPS Handling', v: showDetails.chargesSnapshot.upsAdditionalHandlingUSD },
                                                { l: 'Peak Season Surcharge - LP (UPS Code 431)', v: showDetails.chargesSnapshot.upsPeakSeasonLargePackageUSD },
                                                { l: 'Peak Season Surcharge - AH (UPS Code 432)', v: showDetails.chargesSnapshot.upsPeakSeasonAdditionalHandlingUSD },
                                                { l: 'Remote Origin', v: showDetails.chargesSnapshot.originRemoteAreaUSD },
                                                { l: 'Remote Dest', v: showDetails.chargesSnapshot.destinationRemoteAreaUSD },
                                                { l: 'Peak Period Charges', v: showDetails.chargesSnapshot.peakChargesUSD },
                                                { l: 'Insurance', v: showDetails.chargesSnapshot.upsInsuranceUSD },
                                                { l: 'Others', v: showDetails.chargesSnapshot.upsOthersUSD },
                                            ].map((row, idx) => row.v > 0 && (
                                                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
                                                    <span style={{ color: '#64748b' }}>{row.l}</span>
                                                    <span style={{ fontWeight: 600 }}>${fmt2(row.v)}</span>
                                                </div>
                                            ))}
                                            <div style={{ borderTop: '1px dashed #cbd5e1', margin: '8px 0', paddingTop: 8 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                                                    <span>Subtotal</span>
                                                    <span>${fmt2(showDetails.chargesSnapshot.subtotalUSD)}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b' }}>
                                                    <span>GST ({showDetails.gstOption || '15%'})</span>
                                                    <span>${fmt2(showDetails.chargesSnapshot.gstUSD)}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, color: '#1e293b', marginTop: 10 }}>
                                                    <span>Total</span>
                                                    <span style={{ color: '#4338ca' }}>${fmt2(showDetails.chargesSnapshot.grandTotalUSD)}</span>
                                                </div>
                                                <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                                                    ≈ PKR {fmt2(showDetails.chargesSnapshot.grandTotalUSD * (showDetails.chargesSnapshot.exchangeRate || 280.5))}
                                                </div>
                                            </div>
                                            <div style={{ marginTop: 12, fontSize: 11, background: '#fff', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                                <b>Billing:</b> Paid via {showDetails.paymentMethod?.toUpperCase() || 'CASH'}
                                            </div>
                                        </>
                                    ) : (
                                        <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>
                                            Charges snapshot not available for this legacy record.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="fc-result-actions">
                            {(showDetails.labels?.length > 0 || showDetails.labelImage) && (
                                <button className="fc-result-btn print" onClick={() => handleViewLabel(showDetails)}>🖨️ View/Print Label</button>
                            )}
                            <button className="fc-result-btn close" onClick={() => setShowDetails(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            {showResult && labelResult && (
                <LabelResultModal
                    labelResult={labelResult}
                    onClose={() => { setShowResult(false); setLabelResult(null); }}
                    onPrint={(normalized) => printLabel(normalized)}
                    onDownload={(normalized) => downloadLabel(normalized, labelResult.trackingNumber)}
                    onDownloadReceipt={(data) => downloadReceipt(data)}
                    onDownloadCombined={(labels, data) => downloadCombined(labels, data)}
                />
            )}
        </div>
    );

    /* ══════════════════════════════════════════════
       RENDER — CREATE VIEW (Two-column layout)
    ══════════════════════════════════════════════ */
    return (
        <div className="fc-container">
            <div className="fc-header">
                <button className="fc-back-btn" onClick={() => { setView('list'); resetForm(); }}>← History</button>
                <div className="fc-header-title">
                    <div className="fc-header-icon">✏️</div>
                    <div>
                        <h1>Create FC Label</h1>
                        <p>Fill shipment details on the left, then get rate &amp; create label on the right</p>
                    </div>
                </div>
            </div>

            <div className="fc-content">
                {/* Info banner */}
                <div className="fc-info-row">
                    ℹ️&nbsp;
                    <span>
                        <strong>Import Control Label:</strong> You are creating a label billed to your account.
                        The <strong>Ship From</strong> is the USA customer (origin), and <strong>Ship To</strong> is Pakistan (destination).
                        Rate is looked up by origin country.
                    </span>
                </div>

                {/* TWO-COLUMN LAYOUT */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20, alignItems: 'start' }}>

                    {/* ─── LEFT: Form ─────────────────────────────────── */}
                    <div>
                        {/* Product Type */}
                        <div className="fc-section">
                            <div className="fc-section-title"><span>📋</span>Product Type</div>
                            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                                {PRODUCT_TYPES.map(pt => (
                                    <div
                                        key={pt.code}
                                        onClick={() => setProductType(pt.code)}
                                        style={{
                                            flex: 1, padding: '10px 14px', borderRadius: 10,
                                            border: productType === pt.code ? '2px solid #6366f1' : '2px solid #e2e8f0',
                                            background: productType === pt.code ? '#eef2ff' : '#f8fafc',
                                            cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s'
                                        }}
                                    >
                                        <div style={{ fontSize: 18 }}>{pt.icon}</div>
                                        <div style={{ fontWeight: 700, fontSize: 12, color: '#1e293b', marginTop: 4 }}>{pt.label}</div>
                                    </div>
                                ))}
                            </div>

                        </div>

                        {/* Ship From — USA Customer */}
                        <AddrBlock
                            label="SHIP FROM — USA Customer (Origin) *"
                            icon="🟠"
                            data={shipFrom}
                            onChange={handleShipFromChange}
                            colorBorder="#fb923c"
                            colorBg="#fff7ed"
                            colorText="#c2410c"
                        />

                        {/* Origin Email Notifications */}
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', background: originNotify.enabled ? '#ebf8ff' : '#f8fafc', padding: '8px 12px', borderRadius: '8px', border: originNotify.enabled ? '1px solid #90cdf4' : '1px solid #e2e8f0' }}>
                                <input type="checkbox" id="origin-email-notify" checked={originNotify.enabled} onChange={(e) => setOriginNotify({ ...originNotify, enabled: e.target.checked })} />
                                <label htmlFor="origin-email-notify" style={{ fontWeight: originNotify.enabled ? 'bold' : 'normal', color: originNotify.enabled ? '#2b6cb0' : '#475569', marginLeft: '8px', fontSize: '13px', cursor: 'pointer' }}>Enable Email Notifications for Sender</label>
                            </div>
                            {originNotify.enabled && (
                                <div style={{ display: 'flex', gap: '15px', marginLeft: '30px', marginTop: '8px', fontSize: '12px', color: '#334155' }}>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '4px'}}><input type="checkbox" checked={originNotify.ship} onChange={(e) => setOriginNotify({ ...originNotify, ship: e.target.checked })} /> Creation</label>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '4px'}}><input type="checkbox" checked={originNotify.exception} onChange={(e) => setOriginNotify({ ...originNotify, exception: e.target.checked })} /> Exception</label>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '4px'}}><input type="checkbox" checked={originNotify.delivery} onChange={(e) => setOriginNotify({ ...originNotify, delivery: e.target.checked })} /> Delivery</label>
                                </div>
                            )}
                        </div>

                        {/* Ship To — Pakistan Destination */}
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                position: 'absolute', right: 24, top: -20, zIndex: 10
                            }}>
                                <button
                                    onClick={handleSwitchAddresses}
                                    style={{
                                        width: 40, height: 40, borderRadius: '50%',
                                        background: '#fff', border: '2px solid #e2e8f0',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', fontSize: 18,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        transition: 'all 0.2s', color: '#6366f1'
                                    }}
                                    onMouseEnter={e => { e.target.style.transform = 'scale(1.1) rotate(180deg)'; e.target.style.borderColor = '#6366f1'; }}
                                    onMouseLeave={e => { e.target.style.transform = 'scale(1) rotate(0deg)'; e.target.style.borderColor = '#e2e8f0'; }}
                                    title="Swap Addresses"
                                >
                                    ⇅
                                </button>
                            </div>
                            <AddrBlock
                                label="SHIP TO — Pakistan Destination *"
                                icon="🟢"
                                data={shipTo}
                                onChange={handleShipToChange}
                                colorBorder="#4ade80"
                                colorBg="#f0fdf4"
                                colorText="#15803d"
                            />
                        </div>

                        {/* Destination Email Notifications */}
                        <div style={{ marginBottom: 16, marginTop: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', background: destinationNotify.enabled ? '#ebf8ff' : '#f8fafc', padding: '8px 12px', borderRadius: '8px', border: destinationNotify.enabled ? '1px solid #90cdf4' : '1px solid #e2e8f0' }}>
                                <input type="checkbox" id="dest-email-notify" checked={destinationNotify.enabled} onChange={(e) => setDestinationNotify({ ...destinationNotify, enabled: e.target.checked })} />
                                <label htmlFor="dest-email-notify" style={{ fontWeight: destinationNotify.enabled ? 'bold' : 'normal', color: destinationNotify.enabled ? '#2b6cb0' : '#475569', marginLeft: '8px', fontSize: '13px', cursor: 'pointer' }}>Enable Email Notifications for Receiver</label>
                            </div>
                            {destinationNotify.enabled && (
                                <div style={{ display: 'flex', gap: '15px', marginLeft: '30px', marginTop: '8px', fontSize: '12px', color: '#334155' }}>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '4px'}}><input type="checkbox" checked={destinationNotify.ship} onChange={(e) => setDestinationNotify({ ...destinationNotify, ship: e.target.checked })} /> Creation</label>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '4px'}}><input type="checkbox" checked={destinationNotify.exception} onChange={(e) => setDestinationNotify({ ...destinationNotify, exception: e.target.checked })} /> Exception</label>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '4px'}}><input type="checkbox" checked={destinationNotify.delivery} onChange={(e) => setDestinationNotify({ ...destinationNotify, delivery: e.target.checked })} /> Delivery</label>
                                </div>
                            )}
                        </div>

                        {/* Packages */}
                        <div className="fc-section">
                            <div className="fc-section-title"><span>📦</span>Package Details</div>
                            <div className="fc-pkg-row-header">
                                <span>Weight (KG) *</span>
                                <span>Length (CM){productType !== 'LETTERS' ? ' *' : ''}</span>
                                <span>Width (CM){productType !== 'LETTERS' ? ' *' : ''}</span>
                                <span>Height (CM){productType !== 'LETTERS' ? ' *' : ''}</span>
                                <span>Description</span>
                                <span></span>
                            </div>
                            {packages.map((pkg, i) => (
                                <div key={i} style={{ marginBottom: '0.5rem', paddingBottom: '0.35rem', borderBottom: i < packages.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                                    <div className="fc-pkg-row">
                                        <input className="fc-pkg-input" type="number" min="0" step="0.1"
                                            placeholder="0.5" value={pkg.weight}
                                            onChange={e => updatePkg(i, 'weight', e.target.value)} />
                                        <input className="fc-pkg-input" type="number" min="0" step="0.1"
                                            placeholder={productType === 'LETTERS' ? '—' : '30'}
                                            disabled={productType === 'LETTERS'} value={pkg.length}
                                            onChange={e => updatePkg(i, 'length', e.target.value)} />
                                        <input className="fc-pkg-input" type="number" min="0" step="0.1"
                                            placeholder={productType === 'LETTERS' ? '—' : '20'}
                                            disabled={productType === 'LETTERS'} value={pkg.width}
                                            onChange={e => updatePkg(i, 'width', e.target.value)} />
                                        <input className="fc-pkg-input" type="number" min="0" step="0.1"
                                            placeholder={productType === 'LETTERS' ? '—' : '15'}
                                            disabled={productType === 'LETTERS'} value={pkg.height}
                                            onChange={e => updatePkg(i, 'height', e.target.value)} />
                                        <input className="fc-pkg-input" type="text"
                                            placeholder="Contents" value={pkg.description}
                                            onChange={e => updatePkg(i, 'description', e.target.value)} />
                                        <button className="fc-remove-btn" onClick={() => removePkg(i)}
                                            disabled={packages.length === 1}>✕</button>
                                    </div>
                                    <div style={{ marginTop: '0.25rem', paddingLeft: '0.25rem', display: 'flex', alignItems: 'center' }}>
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#475569', cursor: 'pointer', fontWeight: 600 }}>
                                            <input
                                                type="checkbox"
                                                checked={!!pkg.additionalHandling}
                                                onChange={e => updatePkg(i, 'additionalHandling', e.target.checked)}
                                                style={{ width: '14px', height: '14px', accentColor: '#2563eb', cursor: 'pointer' }}
                                            />
                                            Additional Handling / Irregular Packaging (AHC)
                                        </label>
                                    </div>
                                </div>
                            ))}
                            <button className="fc-add-row-btn" onClick={addPkg}>+ Add Package</button>
                        </div>

                        {/* Description & Reference */}
                        <div className="fc-section">
                            <div className="fc-section-title"><span>📝</span>Description &amp; Insurance</div>
                            <div className="fc-grid-3">
                                <div className="fc-field">
                                    <label>General Description *</label>
                                    <textarea value={generalDescription}
                                        onChange={e => setGeneralDescription(e.target.value)}
                                        placeholder="Contents..."
                                        rows={1} />
                                </div>
                                <div className="fc-field">
                                    <label>Declared Value (USD)</label>
                                    <input type="number" value={declaredValue}
                                        onChange={e => setDeclaredValue(e.target.value)}
                                        placeholder="0.00" style={{ height: 38 }} />
                                </div>
                                <div className="fc-field">
                                    <label>Ref / Note (opt)</label>
                                    <input value={reference1}
                                        onChange={e => setReference1(e.target.value)}
                                        placeholder="Internal ref" style={{ height: 38 }} />
                                </div>
                            </div>

                            {/* Reference 2 Field */}
                            <div className="fc-field" style={{ marginTop: 10 }}>
                                <label>Reference 2 (opt)</label>
                                <input value={reference2}
                                    onChange={e => setReference2(e.target.value)}
                                    placeholder="Additional reference" style={{ height: 38 }} />
                            </div>
                        </div>

                        {/* Form-ready indicator */}
                        <div style={{
                            padding: '10px 14px', borderRadius: 10,
                            background: formReady ? '#ecfdf5' : '#fef3c7',
                            border: `1px solid ${formReady ? '#6ee7b7' : '#fcd34d'}`,
                            fontSize: 12, color: formReady ? '#065f46' : '#92400e',
                            fontWeight: 600, marginTop: 8
                        }}>
                            {formReady
                                ? '✅ Form ready — select an import account on the right to get rate'
                                : '⚠️ Fill all required fields (Name, Address, City, Country, Weight, Description) to enable rate quote'}
                        </div>

                        {/* Create Pickup Checkbox */}
                        {formReady && (
                            <div style={{
                                padding: '12px 14px', borderRadius: 10,
                                background: '#f0fdf4', border: '1px solid #86efac',
                                fontSize: 12, color: '#166534', fontWeight: 500, marginTop: 10,
                                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'
                            }}
                                onClick={() => setCreatePickup(!createPickup)}>
                                <input
                                    type="checkbox"
                                    checked={createPickup}
                                    onChange={() => setCreatePickup(!createPickup)}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                                />
                                <span>📦 Create UPS Pickup after label creation</span>
                                {creatingPickup && <span style={{ marginLeft: 'auto', fontSize: 11 }}>⏳ Creating...</span>}
                            </div>
                        )}
                    </div>

                    {/* ─── RIGHT: Import Account Cards ───────────────── */}
                    <div>
                        <div style={{
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            borderRadius: 14, padding: '14px 18px', marginBottom: 16, color: '#fff'
                        }}>
                            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
                                ✈️ Import Accounts
                            </div>
                            <div style={{ fontSize: 11, opacity: 0.85 }}>
                                Select an account to get rate, receive payment, then create label.
                                Each card tracks its own workflow independently.
                            </div>
                        </div>

                        {importAccounts.length === 0 ? (
                            <div style={{
                                textAlign: 'center', padding: '2rem',
                                background: '#f8fafc', borderRadius: 14,
                                border: '2px dashed #e2e8f0', color: '#94a3b8'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: 8 }}>✈️</div>
                                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>No Import Accounts Found</div>
                                <div style={{ fontSize: 11 }}>
                                    Go to Settings → ✈️ Import Tariffs to upload an import tariff sheet first.
                                </div>
                            </div>
                        ) : (
                            importAccounts.map(acc => (
                                <ImportAccountCard
                                    key={acc}
                                    account={acc}
                                    isSelected={selectedAccount === acc}
                                    onSelect={setSelectedAccount}
                                    formReady={formReady}
                                    shipFrom={shipFrom}
                                    shipTo={shipTo}
                                    packages={packages}
                                    productType={productType}
                                    generalDescription={generalDescription}
                                    reference1={reference1}
                                    reference2={reference2}
                                    declaredValue={declaredValue}
                                    originNotify={originNotify}
                                    destinationNotify={destinationNotify}
                                    currentUser={currentUser}
                                    companySlug={companySlug}
                                    onLabelCreated={(result) => {
                                        setLabelResult(result);
                                        setShowResult(true);
                                        fetchHistory();
                                        // Create pickup if checkbox is checked
                                        if (createPickup && result) {
                                            handleCreatePickup(result);
                                        }
                                    }}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>

            {showResult && labelResult && (
                <LabelResultModal
                    labelResult={labelResult}
                    onClose={() => { setShowResult(false); setLabelResult(null); }}
                    onPrint={(normalized) => printLabel(normalized)}
                    onDownload={(normalized) => downloadLabel(normalized, labelResult.trackingNumber)}
                    onDownloadReceipt={(data) => downloadReceipt(data)}
                    onDownloadCombined={(labels, data) => downloadCombined(labels, data)}
                />
            )}
        </div>
    );
};

export default FCLabel;
